import React, { useRef, useState } from 'react';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import ChartRenderer from './ChartRenderer';
import TableRenderer from './TableRenderer';
import CoordinatedTyping from './CoordinatedTyping';

const ChatMessage = ({ message, onEmailReport, currentUserId, currentUserName }) => {
  const isUser = message.type === 'user';
  // For group chat: show sender name and first letter avatar
  const senderDisplayName = isUser && (message.user_id !== undefined)
    ? (message.user_id === currentUserId ? (currentUserName || 'You') : (message.userName || message.user_name || `User ${message.user_id}`))
    : null;
  const senderInitial = senderDisplayName
    ? (senderDisplayName === 'You' ? (currentUserName ? String(currentUserName).trim().charAt(0).toUpperCase() : 'Y') : String(senderDisplayName).trim().charAt(0).toUpperCase())
    : 'U';
  const reportRef = useRef(null);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const [isGeneratingExcel, setIsGeneratingExcel] = useState(false);

  // Check if this message has visual charts/graphs (not just tables or text)
  // Download and Email buttons should only show when charts are present
  const hasCharts = !isUser && (
    (message.charts && message.charts.length > 0 && message.charts.some(chart => {
      const chartData = Array.isArray(chart.data) ? chart.data : chart.data?.data;
      return Array.isArray(chartData) && chartData.length > 0;
    })) ||
    (message.chartData && message.chartType && (() => {
      const chartData = Array.isArray(message.chartData) ? message.chartData : message.chartData?.data;
      return Array.isArray(chartData) && chartData.length > 0;
    })())
  );

  // Check if this message has any report content (for the report container ref)
  const hasReportContent = !isUser && (
    (message.summaryPoints && message.summaryPoints.length > 0) ||
    (message.charts && message.charts.length > 0) ||
    (message.tables && message.tables.length > 0) ||
    (message.chartData && message.chartType) ||
    (message.kpi_cards && message.kpi_cards.length > 0)
  );

  // Format KPI value with appropriate formatting
  const formatKPIValue = (value, unit) => {
    if (typeof value === 'number') {
      if (unit === '₹' || unit === 'INR' || unit === 'Rs') {
        // Format as Indian currency
        return `₹${value.toLocaleString('en-IN')}`;
      } else if (unit === '%') {
        return `${value.toLocaleString()}%`;
      } else if (unit === 'count' || !unit) {
        return value.toLocaleString('en-IN');
      } else {
        return `${value.toLocaleString('en-IN')} ${unit}`;
      }
    }
    return unit ? `${value} ${unit}` : value;
  };

  // Helper function to thoroughly clean text for PDF rendering
  const cleanTextForPdf = (text) => {
    if (text === null || text === undefined) return '';
    
    // Convert to string first
    const textStr = String(text);
    
    let cleaned = textStr
      // Remove zero-width characters
      .replace(/[\u200B-\u200D\uFEFF\u00AD]/g, '')
      // Remove other invisible/control characters
      .replace(/[\u0000-\u001F\u007F-\u009F]/g, '')
      // Replace smart quotes with regular quotes
      .replace(/[\u2018\u2019]/g, "'")
      .replace(/[\u201C\u201D]/g, '"')
      // Replace en-dash and em-dash with regular dash
      .replace(/[\u2013\u2014]/g, '-')
      // Replace non-breaking space with regular space
      .replace(/\u00A0/g, ' ')
      // Replace multiple spaces with single space
      .replace(/\s+/g, ' ')
      // Normalize the string
      .normalize('NFKC')
      .trim();
    
    return cleaned;
  };

  // Generate PDF from report content
  const generatePdf = async () => {
    if (!reportRef.current) return null;
    
    setIsGeneratingPdf(true);
    console.log('Starting PDF generation...');
    console.log('Summary Points:', message.summaryPoints);
    
    try {
      // Create PDF document with better font handling
      const pdf = new jsPDF({
        orientation: 'p',
        unit: 'mm',
        format: 'a4',
        putOnlyUsedFonts: true,
        compress: true
      });
      
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 15;
      let yPosition = 15;

      // Reset any character spacing
      pdf.setCharSpace(0);
      
      // Add header
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(18);
      pdf.setTextColor(16, 185, 129);
      pdf.text('Finsight Ops', pageWidth / 2, yPosition, { align: 'center' });
      yPosition += 8;
      
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(10);
      pdf.setTextColor(100, 100, 100);
      pdf.text(`Generated on: ${new Date().toLocaleString()}`, pageWidth / 2, yPosition, { align: 'center' });
      yPosition += 5;
      
      // Add line separator
      pdf.setDrawColor(200, 200, 200);
      pdf.line(margin, yPosition, pageWidth - margin, yPosition);
      yPosition += 12;

      // 1. Add Key Insights section - capture from DOM for perfect rendering
      if (message.summaryPoints && message.summaryPoints.length > 0) {
        // Find the summary points container in the DOM
        const summaryContainer = reportRef.current?.querySelector('[data-testid="summary-points"]');
        
        if (summaryContainer) {
          try {
            // Capture summary section as image
            const summaryCanvas = await html2canvas(summaryContainer, {
              scale: 2,
              useCORS: true,
              allowTaint: true,
              backgroundColor: '#ffffff',
              logging: false
            });

            const imgData = summaryCanvas.toDataURL('image/png', 1.0);
            const imgWidth = pageWidth - (margin * 2);
            const imgHeight = (summaryCanvas.height * imgWidth) / summaryCanvas.width;

            // Add section title
            pdf.setFont('helvetica', 'bold');
            pdf.setFontSize(14);
            pdf.setTextColor(30, 64, 175);
            pdf.text('Key Insights', margin, yPosition);
            yPosition += 8;

            // Add captured image
            pdf.addImage(imgData, 'PNG', margin, yPosition, imgWidth, Math.min(imgHeight, 80));
            yPosition += Math.min(imgHeight, 80) + 10;
          } catch (err) {
            console.error('Error capturing summary:', err);
            // Fallback to text-based rendering
            pdf.setFont('helvetica', 'bold');
            pdf.setFontSize(14);
            pdf.setTextColor(30, 64, 175);
            pdf.text('Key Insights', margin, yPosition);
            yPosition += 8;

            message.summaryPoints.forEach((point) => {
              const cleanText = cleanTextForPdf(point);
              pdf.setFont('helvetica', 'normal');
              pdf.setFontSize(10);
              pdf.setTextColor(50, 50, 50);
              const lines = pdf.splitTextToSize(`• ${cleanText}`, pageWidth - (margin * 2));
              pdf.text(lines, margin, yPosition);
              yPosition += lines.length * 5 + 3;
            });
            yPosition += 5;
          }
        } else {
          // Fallback: render text directly
          pdf.setFont('helvetica', 'bold');
          pdf.setFontSize(14);
          pdf.setTextColor(30, 64, 175);
          pdf.text('Key Insights', margin, yPosition);
          yPosition += 8;

          message.summaryPoints.forEach((point) => {
            if (yPosition > pageHeight - 30) {
              pdf.addPage();
              yPosition = 20;
            }
            const cleanText = cleanTextForPdf(point);
            pdf.setFont('helvetica', 'normal');
            pdf.setFontSize(10);
            pdf.setTextColor(50, 50, 50);
            const lines = pdf.splitTextToSize(`• ${cleanText}`, pageWidth - (margin * 2));
            pdf.text(lines, margin, yPosition);
            yPosition += lines.length * 5 + 3;
          });
          yPosition += 5;
        }
      }

      // 2. Add Charts section with data values
      if (message.charts && message.charts.length > 0) {
        const chartContainers = reportRef.current.querySelectorAll('.recharts-wrapper');
        
        for (let i = 0; i < message.charts.length; i++) {
          const currentChart = message.charts[i];
          const chartData = Array.isArray(currentChart.data) ? currentChart.data : currentChart.data?.data;
          
          if (!chartData || chartData.length === 0) continue;
          
          // Check if we need a new page
          if (yPosition > pageHeight - 140) {
            pdf.addPage();
            yPosition = 20;
          }

          try {
            // Add chart title
            if (currentChart?.title) {
              pdf.setFont('helvetica', 'bold');
              pdf.setFontSize(12);
              pdf.setTextColor(30, 64, 175);
              pdf.setCharSpace(0);
              pdf.text(cleanTextForPdf(currentChart.title), margin, yPosition);
              yPosition += 7;
            }

            // Add chart description
            if (currentChart?.description) {
              pdf.setFont('helvetica', 'normal');
              pdf.setFontSize(9);
              pdf.setTextColor(80, 80, 80);
              pdf.setCharSpace(0);
              const cleanDesc = cleanTextForPdf(currentChart.description);
              const descriptionLines = pdf.splitTextToSize(cleanDesc, pageWidth - (margin * 2));
              pdf.text(descriptionLines, margin, yPosition);
              yPosition += (descriptionLines.length * 4) + 4;
            }

            const chartType = currentChart.type?.toLowerCase();

            // Capture chart image
            if (chartContainers[i]) {
              const canvas = await html2canvas(chartContainers[i], {
                scale: 2,
                useCORS: true,
                allowTaint: true,
                backgroundColor: '#ffffff',
                logging: false
              });

              const imgData = canvas.toDataURL('image/png', 1.0);
              
              // Calculate proper dimensions maintaining aspect ratio
              const originalWidth = canvas.width;
              const originalHeight = canvas.height;
              const aspectRatio = originalWidth / originalHeight;
              
              let imgWidth, imgHeight;
              const maxWidth = pageWidth - (margin * 2);
              const maxHeight = 65; // Max height for chart
              
              if (chartType === 'pie' || chartType === 'donut') {
                // For pie/donut, use square-ish dimensions
                imgHeight = Math.min(maxHeight, 60);
                imgWidth = imgHeight * aspectRatio;
                if (imgWidth > maxWidth * 0.6) {
                  imgWidth = maxWidth * 0.6;
                  imgHeight = imgWidth / aspectRatio;
                }
                // Center the pie chart
                const chartX = margin + (maxWidth - imgWidth) / 2;
                pdf.addImage(imgData, 'PNG', chartX, yPosition, imgWidth, imgHeight);
              } else {
                // For bar/line charts, use full width
                imgWidth = maxWidth;
                imgHeight = imgWidth / aspectRatio;
                if (imgHeight > maxHeight) {
                  imgHeight = maxHeight;
                  imgWidth = imgHeight * aspectRatio;
                }
                pdf.addImage(imgData, 'PNG', margin, yPosition, imgWidth, imgHeight);
              }
              
              yPosition += imgHeight + 5;
            }

            // Add data values table below chart
            const dataRows = chartData.map(item => {
              const name = cleanTextForPdf(item.name || item[Object.keys(item)[0]] || 'Unknown');
              const value = item.value || item[Object.keys(item)[1]] || 0;
              const formattedValue = typeof value === 'number' ? value.toLocaleString() : value;
              return [name, formattedValue];
            });

            // Calculate totals for percentage (for pie/donut)
            const total = chartData.reduce((sum, item) => {
              const value = item.value || item[Object.keys(item)[1]] || 0;
              return sum + Number(value);
            }, 0);

            // Add percentage column for pie/donut charts
            const tableHeaders = chartType === 'pie' || chartType === 'donut' 
              ? ['Category', 'Value', '%'] 
              : ['Category', 'Value'];
            
            const tableData = chartType === 'pie' || chartType === 'donut'
              ? dataRows.map(row => {
                  const val = parseFloat(String(row[1]).replace(/,/g, '')) || 0;
                  const pct = total > 0 ? ((val / total) * 100).toFixed(1) + '%' : '0%';
                  return [...row, pct];
                })
              : dataRows;

            autoTable(pdf, {
              startY: yPosition,
              head: [tableHeaders],
              body: tableData,
              margin: { left: margin, right: margin },
              styles: {
                font: 'helvetica',
                fontSize: 8,
                cellPadding: 2,
                overflow: 'linebreak',
                lineColor: [220, 220, 220],
                lineWidth: 0.1,
              },
              headStyles: {
                fillColor: [241, 245, 249],
                textColor: [51, 65, 85],
                fontStyle: 'bold',
                fontSize: 8,
                halign: 'left',
              },
              bodyStyles: {
                fillColor: [255, 255, 255],
                textColor: [30, 41, 59],
                halign: 'left',
              },
              alternateRowStyles: {
                fillColor: [248, 250, 252],
              },
              columnStyles: chartType === 'pie' || chartType === 'donut'
                ? { 0: { cellWidth: 60 }, 1: { cellWidth: 40, halign: 'right' }, 2: { cellWidth: 25, halign: 'right' } }
                : { 0: { cellWidth: 80 }, 1: { cellWidth: 50, halign: 'right' } },
              tableWidth: 'auto',
            });

            yPosition = (pdf.lastAutoTable?.finalY || yPosition + 30) + 12;
            
          } catch (chartError) {
            console.error('Error capturing chart:', chartError);
          }
        }
      }

      // 3. Add Tables section - use autoTable for reliable rendering
      if (message.tables && message.tables.length > 0) {
        for (let i = 0; i < message.tables.length; i++) {
          const table = message.tables[i];
          if (!table.headers || !table.rows || table.headers.length === 0 || table.rows.length === 0) {
            continue;
          }

          // Check if we need a new page
          if (yPosition > pageHeight - 80) {
            pdf.addPage();
            yPosition = 20;
          }

          // Add table title
          if (table.title) {
            pdf.setFont('helvetica', 'bold');
            pdf.setFontSize(12);
            pdf.setTextColor(30, 64, 175);
            pdf.setCharSpace(0);
            pdf.text(cleanTextForPdf(table.title), margin, yPosition);
            yPosition += 8;
          }

          // Use autoTable directly for reliable text rendering (avoids html2canvas font issues)
          const cleanHeaders = table.headers.map(h => cleanTextForPdf(h).replace(/₹/g, 'Rs.'));
          const cleanRows = table.rows.map(row => 
            row.map(cell => cleanTextForPdf(cell).replace(/₹/g, 'Rs.'))
          );

          autoTable(pdf, {
            startY: yPosition,
            head: [cleanHeaders],
            body: cleanRows,
            margin: { left: margin, right: margin },
            styles: {
              font: 'helvetica',
              fontSize: 9,
              cellPadding: 4,
              overflow: 'linebreak',
              lineColor: [220, 220, 220],
              lineWidth: 0.2,
              valign: 'middle',
            },
            headStyles: {
              fillColor: [241, 245, 249],
              textColor: [51, 65, 85],
              fontStyle: 'bold',
              fontSize: 9,
              halign: 'left',
            },
            bodyStyles: {
              fillColor: [255, 255, 255],
              textColor: [30, 41, 59],
              halign: 'left',
            },
            alternateRowStyles: {
              fillColor: [248, 250, 252],
            },
            tableWidth: 'auto',
            showHead: 'everyPage',
          });

          yPosition = (pdf.lastAutoTable?.finalY || yPosition + 50) + 15;
        }
      }

      // 4. Handle single chartData/chartType (legacy format)
      if (!message.charts && message.chartData && message.chartType) {
        const chartEl = reportRef.current.querySelector('.recharts-wrapper');
        if (chartEl) {
          if (yPosition > pageHeight - 100) {
            pdf.addPage();
            yPosition = 20;
          }

          try {
            const canvas = await html2canvas(chartEl, {
              scale: 2,
              useCORS: true,
              allowTaint: true,
              backgroundColor: '#ffffff',
              logging: false
            });

            const imgData = canvas.toDataURL('image/png', 1.0);
            const imgWidth = pageWidth - (margin * 2);
            const imgHeight = (canvas.height * imgWidth) / canvas.width;

            pdf.setFont('helvetica', 'bold');
            pdf.setFontSize(11);
            pdf.setTextColor(60, 60, 60);
            pdf.text('Data Visualization', margin, yPosition);
            yPosition += 6;

            pdf.addImage(imgData, 'PNG', margin, yPosition, imgWidth, Math.min(imgHeight, 100));
          } catch (chartError) {
            console.error('Error capturing chart:', chartError);
          }
        }
      }

      return pdf;
    } catch (error) {
      console.error('Error generating PDF:', error);
      return null;
    } finally {
      setIsGeneratingPdf(false);
    }
  };

  // Helper function to convert hex color to RGB
  const hexToRgb = (hex) => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? [
      parseInt(result[1], 16),
      parseInt(result[2], 16),
      parseInt(result[3], 16)
    ] : [100, 100, 100];
  };

  // Handle download button click
  const handleDownload = async () => {
    try {
      const pdf = await generatePdf();
      if (pdf) {
        const timestamp = new Date().toISOString().slice(0, 10);
        pdf.save(`Report_${timestamp}.pdf`);
      }
    } catch (error) {
      console.error('Error downloading PDF:', error);
      setIsGeneratingPdf(false);
    }
  };

  // Handle email button click
  const handleEmail = async () => {
    try {
      const pdf = await generatePdf();
      if (pdf && onEmailReport) {
        // Convert PDF to base64
        const pdfBase64 = pdf.output('datauristring').split(',')[1];
        const timestamp = new Date().toISOString().slice(0, 10);
        onEmailReport({
          pdfBase64,
          filename: `Report_${timestamp}.pdf`
        });
      }
    } catch (error) {
      console.error('Error preparing email:', error);
      setIsGeneratingPdf(false);
    }
  };

  // Generate Excel with all data including charts (for single table export via backend)
  const generateExcel = async (tableData = null, tableIndex = null) => {
    setIsGeneratingExcel(true);
    
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL || '';
      
      // If specific table is provided, export only that table via backend API
      if (tableData && tableIndex !== null) {
        const requestData = {
          headers: tableData.headers || [],
          rows: tableData.rows || [],
          full_data: tableData.full_data || null,
          title: tableData.title || `Table ${tableIndex + 1}`,
          description: tableData.description || '',
          sql_query: tableData.sql_query || null
        };
        
        const response = await fetch(`${API_URL}/api/export/excel/table`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(requestData)
        });
        
        if (!response.ok) {
          throw new Error('Failed to export table');
        }
        
        // Get the blob from response
        const blob = await response.blob();
        
        // Get filename from Content-Disposition header or use default
        const contentDisposition = response.headers.get('Content-Disposition');
        let filename = `Table_${new Date().toISOString().slice(0, 10)}.xlsx`;
        if (contentDisposition) {
          const filenameMatch = contentDisposition.match(/filename=(.+)/);
          if (filenameMatch) {
            filename = filenameMatch[1].replace(/"/g, '');
          }
        }
        
        // Create download link and trigger download
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        
        setIsGeneratingExcel(false);
        return;
      }
      
      // For non-table specific exports, use the old XLSX method as fallback
      const workbook = XLSX.utils.book_new();
      const timestamp = new Date().toISOString().slice(0, 10);
      
      // Helper function to clean filename - remove row count patterns
      const cleanFilename = (name) => {
        if (!name) return '';
        return name
          .replace(/top\s*\d+\s*/gi, '')
          .replace(/first\s*\d+\s*/gi, '')
          .replace(/bottom\s*\d+\s*/gi, '')
          .replace(/^\d+\s+/g, '')
          .replace(/^[\s_]+|[\s_]+$/g, '')
          .replace(/[\s_]+/g, '_')
          .replace(/[\\/*?[\]]/g, '');
      };
      
      // Export all tables and charts from the message (excluding summary)
      let sheetCount = 0;
      
      // Add Charts data as sheets
      if (message.charts && message.charts.length > 0) {
        message.charts.forEach((chart, index) => {
          const chartData = Array.isArray(chart.data) ? chart.data : chart.data?.data;
          if (Array.isArray(chartData) && chartData.length > 0) {
            const keys = Object.keys(chartData[0]);
            const wsData = [keys, ...chartData.map(item => keys.map(key => item[key]))];
            const worksheet = XLSX.utils.aoa_to_sheet(wsData);
            
            const colWidths = keys.map((key, i) => {
              const maxLen = Math.max(
                key.length,
                ...chartData.map(item => String(item[key] || '').length)
              );
              return { wch: Math.min(maxLen + 2, 30) };
            });
            worksheet['!cols'] = colWidths;
            
            const cleanedTitle = cleanFilename(chart.title) || `Chart_${index + 1}`;
            const sheetName = cleanedTitle.substring(0, 31);
            XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
            sheetCount++;
          }
        });
      }
      
      // Add Tables data as sheets - use full_data for all records
      if (message.tables && message.tables.length > 0) {
        message.tables.forEach((table, index) => {
          if (table.headers && (table.full_data || table.rows)) {
            // Always use full_data if available for complete data export
            const allRows = table.full_data || table.rows || [];
            const wsData = [table.headers, ...allRows];
            const worksheet = XLSX.utils.aoa_to_sheet(wsData);
            
            const colWidths = table.headers.map((header, i) => {
              const maxLen = Math.max(
                header.length,
                ...allRows.slice(0, 100).map(row => String(row[i] || '').length)
              );
              return { wch: Math.min(maxLen + 2, 50) };
            });
            worksheet['!cols'] = colWidths;
            
            const cleanedTitle = cleanFilename(table.title) || `Table_${index + 1}`;
            const sheetName = cleanedTitle.substring(0, 31);
            XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
            sheetCount++;
          }
        });
      }
      
      if (sheetCount > 0) {
        XLSX.writeFile(workbook, `FinSight_Report_${timestamp}.xlsx`);
      }
      
    } catch (error) {
      console.error('Error generating Excel:', error);
    } finally {
      setIsGeneratingExcel(false);
    }
  };

  // Handle Excel export for specific table
  const handleExportTableToExcel = (table, index) => {
    generateExcel(table, index);
  };

  // Generate complete Excel report using backend API
  const generateCompleteExcelReport = async () => {
    setIsGeneratingExcel(true);
    
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL || '';
      
      // Prepare report data for backend
      // Download Excel only exports displayed rows (not full_data)
      const reportData = {
        summary_points: message.summaryPoints || [],
        content: message.content || '',
        charts: (message.charts || []).map(chart => ({
          type: chart.type || 'bar',
          title: chart.title || 'Chart',
          description: chart.description || '',
          data: Array.isArray(chart.data) ? chart.data : (chart.data?.data || [])
        })),
        tables: (message.tables || []).map(table => ({
          headers: table.headers || [],
          rows: table.rows || [],  // Only displayed rows
          full_data: null,  // Do NOT include full_data for Download Excel
          title: table.title || 'Table',
          description: table.description || ''
        }))
      };
      
      // Call backend API to generate Excel
      const response = await fetch(`${API_URL}/api/export/excel`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(reportData)
      });
      
      if (!response.ok) {
        throw new Error('Failed to generate Excel report');
      }
      
      // Get the blob from response
      const blob = await response.blob();
      
      // Get filename from Content-Disposition header or use default
      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = `FinSight_Report_${new Date().toISOString().slice(0, 10)}.xlsx`;
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename=(.+)/);
        if (filenameMatch) {
          filename = filenameMatch[1].replace(/"/g, '');
        }
      }
      
      // Create download link and trigger download
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
    } catch (error) {
      console.error('Error generating Excel report:', error);
      alert('Failed to generate Excel report. Please try again.');
    } finally {
      setIsGeneratingExcel(false);
    }
  };

  return (
    <div className={`flex ${isUser ? 'justify-end' : 'justify-start'} fade-in`}>
      <div className={`w-full ${isUser ? 'text-right' : 'text-left'}`}>
        {isUser && (
          <div className="flex items-start space-x-3 mb-3">
            <div className="flex-1 flex flex-col items-end">
              <div className="chat-bubble-user">
                <p className="leading-relaxed">{message.content}</p>
              </div>
              {senderDisplayName && (
                <span className="text-xs text-slate-500 mt-1 mr-1">
                  {senderDisplayName}
                </span>
              )}
            </div>
            <div className="w-8 h-8 bg-slate-200 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
              <span className="text-slate-600 text-sm font-medium">{senderInitial}</span>
            </div>
          </div>
        )}

        {!isUser && (
          <div className="flex items-start space-x-3 mb-3">
            <div className="w-8 h-8 bg-gradient-primary rounded-full flex items-center justify-center flex-shrink-0 mt-1">
            </div>
            <div className="flex-1 min-w-0">
              
              {/* Report Content Container - This will be captured for PDF */}
              <div ref={reportRef} className="report-content">
                {/* Show response message when summaryPoints is empty and content is not raw JSON */}
                {(!message.summaryPoints || message.summaryPoints.length === 0) && 
                 message.content && 
                 !message.content.trim().startsWith('{') && 
                 !message.content.trim().startsWith('[') && (
                  <div className="mb-4">
                    <div className="bg-white/60 backdrop-blur-sm rounded-2xl shadow-sm border border-white/20 p-4">
                      <div className="flex items-center mb-3">
                        <div className="w-8 h-8 bg-gradient-to-r from-slate-500 to-slate-600 rounded-xl flex items-center justify-center mr-3 shadow-sm">
                          <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                          </svg>
                        </div>
                        <h4 className="font-semibold text-slate-700 text-sm">Response</h4>
                      </div>
                      <p className="text-slate-700 text-sm leading-relaxed">{message.content}</p>
                    </div>
                  </div>
                )}

                {message.summaryPoints && message.summaryPoints.length > 0 && (
                  <div className="mb-4">
                    <div data-testid="summary-points" className="bg-white/60 backdrop-blur-sm rounded-2xl shadow-sm border border-white/20 p-4">
                      <div className="flex items-center mb-3">
                        <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-xl flex items-center justify-center mr-3 shadow-sm">
                          <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                          </svg>
                        </div>
                        <h4 className="font-semibold text-blue-900 text-sm">Key Insights</h4>
                      </div>
                      <div className="space-y-2">
                        <CoordinatedTyping 
                          summaryPoints={message.summaryPoints}
                          speed={20}
                          initialDelay={800}
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* KPI Cards */}
                {message.kpi_cards && message.kpi_cards.length > 0 && (
                  <div className="mb-6">
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" data-testid="kpi-cards">
                      {message.kpi_cards.map((kpi, index) => (
                        <div
                          key={index}
                          className="bg-gradient-to-br from-white to-slate-50 rounded-2xl shadow-sm border border-slate-200 p-5 hover:shadow-md transition-shadow duration-200"
                          data-testid={`kpi-card-${index}`}
                        >
                          <div className="flex flex-col">
                            <span className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-2">
                              {kpi.label}
                            </span>
                            <span className="text-2xl font-bold text-slate-800">
                              {formatKPIValue(kpi.value, kpi.unit)}
                            </span>
                            {kpi.unit && kpi.unit !== '₹' && kpi.unit !== 'INR' && kpi.unit !== 'Rs' && kpi.unit !== '%' && kpi.unit !== 'count' && (
                              <span className="text-xs text-slate-400 mt-1">{kpi.unit}</span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {message.charts && message.charts.length > 0 && message.charts.some(chart => {
                  const chartData = Array.isArray(chart.data) ? chart.data : chart.data?.data;
                  return Array.isArray(chartData) && chartData.length > 0;
                }) && (
                  <div className="mb-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-2 gap-4 md:gap-6 auto-rows-fr">
                      {message.charts.filter(chart => {
                        const chartData = Array.isArray(chart.data) ? chart.data : chart.data?.data;
                        return Array.isArray(chartData) && chartData.length > 0;
                      }).map((chart, index) => (
                        <div 
                          key={index} 
                          className={`bg-white/60 backdrop-blur-sm rounded-2xl shadow-sm border border-white/20 overflow-hidden flex flex-col ${
                            message.charts.length === 1 ? 'md:col-span-2 xl:col-span-1' : ''
                          }`}
                        >
                          <div className="p-4 flex-1 flex flex-col">
                            <ChartRenderer
                              data={chart.data}
                              type={chart.type}
                              title={chart.title}
                              description={chart.description}
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {message.tables && message.tables.length > 0 && message.tables.some(table => {
                  return table.headers && Array.isArray(table.headers) && table.headers.length > 0 &&
                         table.rows && Array.isArray(table.rows) && table.rows.length > 0;
                }) && (
                  <div className="mb-1">
                    {message.tables.filter(table => {
                      return table.headers && Array.isArray(table.headers) && table.headers.length > 0 &&
                             table.rows && Array.isArray(table.rows) && table.rows.length > 0;
                    }).map((table, index) => (
                      <div 
                        key={index}
                        data-testid={`table-container-${index}`}
                        className="bg-white/60 backdrop-blur-sm rounded-2xl shadow-sm border border-white/20 overflow-hidden mb-4 last:mb-0"
                      >
                        <div className="w-full overflow-x-auto">
                          <TableRenderer
                            data={table}
                            title={table.title}
                            description={table.description}
                            onExportExcel={() => handleExportTableToExcel(table, index)}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {!message.charts && message.chartData && message.chartType && (() => {
                  const chartData = Array.isArray(message.chartData) ? message.chartData : message.chartData?.data;
                  return Array.isArray(chartData) && chartData.length > 0;
                })() && (
                  <div className="mb-4">
                    <div className="bg-white/60 backdrop-blur-sm rounded-2xl shadow-sm border border-white/20 overflow-hidden">
                      <div className="bg-gradient-to-r from-slate-50 to-blue-50 px-4 py-3 border-b border-slate-200">
                        <div className="flex items-center">
                          <div className="w-8 h-8 bg-gradient-to-r from-indigo-500 to-purple-500 rounded-xl flex items-center justify-center mr-3 shadow-sm">
                            <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                            </svg>
                          </div>
                          <div>
                            <h4 className="font-semibold text-slate-700 text-sm">Data Visualization</h4>
                            <span className="text-xs text-slate-500 bg-white px-2 py-1 rounded-full">
                              {message.chartType.toUpperCase()}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="p-4">
                        <ChartRenderer
                          data={message.chartData}
                          type={message.chartType}
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Download and Email Buttons - Icon with tooltip */}
              {hasReportContent && (
                <div className="mt-1 flex items-center space-x-1 flex-wrap gap-1">
                  {/* PDF Download */}
                  <button
                    onClick={handleDownload}
                    disabled={isGeneratingPdf}
                    data-testid="download-pdf-btn"
                    title={isGeneratingPdf ? 'Generating...' : 'Download PDF'}
                    className="flex items-center justify-center w-9 h-9 text-black rounded-lg hover:bg-slate-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isGeneratingPdf ? (
                      <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                    ) : (
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                      </svg>
                    )}
                  </button>

                  {/* Excel Download */}
                  <button
                    onClick={generateCompleteExcelReport}
                    disabled={isGeneratingExcel}
                    data-testid="download-excel-btn"
                    title={isGeneratingExcel ? 'Exporting...' : 'Download Excel'}
                    className="flex items-center justify-center w-9 h-9 text-black rounded-lg hover:bg-slate-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isGeneratingExcel ? (
                      <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                    ) : (
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                    )}
                  </button>

                  {/* Send Email */}
                  <button
                    onClick={handleEmail}
                    disabled={isGeneratingPdf}
                    data-testid="email-report-btn"
                    title={isGeneratingPdf ? 'Preparing...' : 'Send Email'}
                    className="flex items-center justify-center w-9 h-9 text-black rounded-lg hover:bg-slate-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isGeneratingPdf ? (
                      <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                    ) : (
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                    )}
                  </button>
                </div>
              )}

            </div>
          </div>
        )}

        <div className={`text-xs text-slate-400 mt-1 ${isUser ? 'text-right mr-11' : 'text-left ml-11'
          }`}>
          {message.timestamp.toLocaleTimeString([], {
            hour: '2-digit',
            minute: '2-digit'
          })}
        </div>
      </div>
    </div>
  );
};

export default ChatMessage;
